/*
 * Durnev Overlays — declarative open/close for drawers, dropdowns, and modals.
 *
 * Markup contract:
 *   <button data-overlay-open="<id>" aria-expanded="false">Trigger</button>
 *   <div class="durnev-overlay" id="overlay-<id>" aria-hidden="true">
 *     <div class="durnev-overlay__backdrop" data-overlay-close></div>
 *     <div class="durnev-overlay__panel">
 *       <button class="durnev-overlay__close" data-overlay-close>×</button>
 *       <!-- panel content -->
 *     </div>
 *   </div>
 *
 * Open by:  data-overlay-open="<id>" attribute on a trigger element
 * Close by: data-overlay-close attribute (anywhere inside the overlay)
 *           OR pressing Escape
 *           OR opening another overlay (only one open at a time)
 *
 * Side effects on open:
 *  - <html> gets `durnev-overlay-open` class (lock body scroll via CSS)
 *  - target overlay gets `is-open` class, aria-hidden=false
 *  - trigger's aria-expanded becomes true
 *  - focus moves to first focusable inside panel; restored on close
 */
(function () {
  'use strict';

  var openId = null;
  var lastFocus = null;
  var savedScrollY = 0;

  function getOverlay(id) {
    return document.getElementById('overlay-' + id);
  }

  function getTrigger(id) {
    return document.querySelector('[data-overlay-open="' + id + '"], [data-overlay-hover="' + id + '"]');
  }

  function focusable(panel) {
    if (!panel) return null;
    // Prefer first text/search input or textarea over buttons (skip the close-X)
    var preferred = panel.querySelector(
      'input[type="text"]:not([disabled]), input[type="search"]:not([disabled]), input[type="email"]:not([disabled]), input[type="tel"]:not([disabled]), input[type="password"]:not([disabled]), textarea:not([disabled])'
    );
    if (preferred) return preferred;
    // Fallback: first focusable that is not a close-button
    var candidates = panel.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    for (var i = 0; i < candidates.length; i++) {
      if (!candidates[i].closest('[data-overlay-close]') && !candidates[i].hasAttribute('data-overlay-close')) {
        return candidates[i];
      }
    }
    return candidates[0] || null;
  }

  // Anchor the compact account dropdown so its left edge sits flush with
  // the АККАУНТ trigger and its right edge stretches to the right end of
  // the nav (UA(EN) link, or the nav block itself if there's no lang
  // switcher) — matches the Figma layout.
  function anchorAccountDropdown() {
    var trigger = document.querySelector('[data-overlay-open="account"]');
    var overlay = getOverlay('account');
    if (!trigger || !overlay) return;
    var panel = overlay.querySelector('.durnev-overlay__panel--account');
    if (!panel) return;
    var nav = trigger.closest('.durnev-header__nav');
    if (!nav) return;
    var triggerRect = trigger.getBoundingClientRect();
    // Reference the overlay's own bounding rect, not window.innerWidth — when
    // scrollbar-gutter:stable reserves a strip, the fixed-positioned overlay
    // is narrower than the window by exactly that gutter.
    var overlayRect = overlay.getBoundingClientRect();
    // Right edge: flush against the right end of the overlay (= viewport
    // edge minus scrollbar gutter). The header has horizontal padding so
    // UA(EN).right doesn't reach the viewport edge — the dropdown should.
    panel.style.left = (triggerRect.left - overlayRect.left) + 'px';
    panel.style.right = '0px';
    panel.style.width = 'auto';
  }

  // Hover-driven dropdowns (mega-menu, account) shouldn't take over the
  // page — the user should still be able to scroll while they hover. Only
  // click-only modal overlays (search, cart) lock body scroll. We detect
  // hover-driven overlays by checking the trigger for data-overlay-hover.
  function shouldLockScroll(id) {
    // Cart drawer is a side panel — the rest of the page should stay
    // scrollable and clickable behind it so users can keep browsing
    // (and clicking anywhere outside the panel closes the drawer via
    // the doc-level mousedown handler below).
    if (id === 'cart') return false;
    var trigger = getTrigger(id);
    return !(trigger && trigger.hasAttribute('data-overlay-hover'));
  }

  function open(id) {
    if (openId === id) return;
    if (openId) close();
    var overlay = getOverlay(id);
    if (!overlay) return;
    var trigger = getTrigger(id);

    if (id === 'account') anchorAccountDropdown();

    overlay.classList.add('is-open');
    overlay.setAttribute('aria-hidden', 'false');
    if (shouldLockScroll(id)) {
      // iOS Safari: overflow:hidden on <html> alone doesn't stop body scroll.
      // Standard fix: snapshot scrollY, freeze body via position:fixed at
      // negative top offset, restore on close.
      savedScrollY = window.scrollY || window.pageYOffset || 0;
      document.documentElement.classList.add('durnev-overlay-open');
      document.body.style.position = 'fixed';
      document.body.style.top = '-' + savedScrollY + 'px';
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';
    }
    if (trigger) trigger.setAttribute('aria-expanded', 'true');

    lastFocus = document.activeElement;
    var panel = overlay.querySelector('.durnev-overlay__panel');
    // Hover-driven dropdowns (mega-menu) shouldn't steal keyboard focus —
    // the user hasn't requested it, and the focus ring around the first
    // link looked like an unwanted highlight on every page load.
    if (shouldLockScroll(id)) {
      var first = focusable(panel);
      if (first) {
        requestAnimationFrame(function () { try { first.focus({ preventScroll: true }); } catch (e) { first.focus(); } });
      }
    }

    openId = id;
    overlay.dispatchEvent(new CustomEvent('durnev:overlay-open', { bubbles: true, detail: { id: id } }));
  }

  function close() {
    if (!openId) return;
    var overlay = getOverlay(openId);
    var trigger = getTrigger(openId);

    if (overlay) {
      overlay.classList.remove('is-open');
      overlay.setAttribute('aria-hidden', 'true');
      overlay.dispatchEvent(new CustomEvent('durnev:overlay-close', { bubbles: true, detail: { id: openId } }));
    }
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    if (document.documentElement.classList.contains('durnev-overlay-open')) {
      document.documentElement.classList.remove('durnev-overlay-open');
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.width = '';
      window.scrollTo(0, savedScrollY);
    }

    if (lastFocus && typeof lastFocus.focus === 'function') {
      try { lastFocus.focus({ preventScroll: true }); } catch (e) { lastFocus.focus(); }
    }
    lastFocus = null;
    openId = null;
  }

  function toggle(id) {
    if (openId === id) close();
    else open(id);
  }

  // Click delegate
  document.addEventListener('click', function (e) {
    var openTrigger = e.target.closest('[data-overlay-open]');
    if (openTrigger) {
      var id = openTrigger.getAttribute('data-overlay-open');
      if (id) {
        e.preventDefault();
        toggle(id);
      }
      return;
    }
    var closeTrigger = e.target.closest('[data-overlay-close]');
    if (closeTrigger) {
      e.preventDefault();
      close();
    }
  });

  // Middle-click (button:1) on an overlay trigger would otherwise let
  // the browser follow the anchor's href in a new tab — bypassing the
  // overlay UI entirely. Cancel it on both mousedown (Safari initiates
  // the new-tab there) and auxclick (Chromium/Firefox finalise it there).
  function blockAuxOpen(e) {
    if (e.button !== 1) return;
    if (e.target.closest('[data-overlay-open]')) e.preventDefault();
  }
  document.addEventListener('mousedown', blockAuxOpen);
  document.addEventListener('auxclick', blockAuxOpen);

  // Hover delegate (desktop pointer-fine devices only — mega-menu UX)
  // data-overlay-hover="<id>" → open on mouseenter on trigger or overlay,
  // close on mouseleave from BOTH (with small grace delay so user can
  // travel between trigger and panel without losing the menu).
  var hoverTimers = {};
  function cancelClose(id) {
    if (hoverTimers[id]) {
      clearTimeout(hoverTimers[id]);
      delete hoverTimers[id];
    }
  }
  function scheduleClose(id, delay) {
    cancelClose(id);
    hoverTimers[id] = setTimeout(function () {
      if (openId === id) close();
      delete hoverTimers[id];
    }, delay || 180);
  }
  function bindHover() {
    if (!window.matchMedia || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    var triggers = document.querySelectorAll('[data-overlay-hover]');
    triggers.forEach(function (trigger) {
      if (trigger.__durnevHoverBound) return;
      trigger.__durnevHoverBound = true;
      var id = trigger.getAttribute('data-overlay-hover');
      if (!id) return;
      var overlay = getOverlay(id);
      if (!overlay) return;
      var panel = overlay.querySelector('.durnev-overlay__panel');
      trigger.addEventListener('mouseenter', function () { cancelClose(id); open(id); });
      trigger.addEventListener('mouseleave', function () { scheduleClose(id); });
      // Bind to the visible panel, not the full-viewport overlay container —
      // otherwise a transparent or full-screen backdrop swallows mouseleave
      // and the dropdown stays open until cursor leaves the page entirely.
      if (panel) {
        panel.addEventListener('mouseenter', function () { cancelClose(id); });
        panel.addEventListener('mouseleave', function () { scheduleClose(id); });
      }
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindHover);
  } else {
    bindHover();
  }

  // Anchor the mega-menu / account dropdown / drawers to the BOTTOM edge of
  // the header rather than just its height. When the announcement bar sits
  // above the header it pushes the header down, so the dropdown must open
  // lower — using the live bottom edge (which already accounts for the bar
  // and the current scroll position) keeps it flush in every state.
  function syncHeaderHeight() {
    var header = document.querySelector('.section-durnev-header');
    if (!header) return;
    var bottom = header.getBoundingClientRect().bottom;
    if (bottom > 0) document.documentElement.style.setProperty('--durnev-header-height', bottom + 'px');
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncHeaderHeight);
  } else {
    syncHeaderHeight();
  }
  window.addEventListener('resize', syncHeaderHeight);
  window.addEventListener('load', syncHeaderHeight);
  // The announcement bar scrolls away while the header stays sticky, so the
  // header's bottom edge shifts during the first bit of scroll — keep the var
  // in step (rAF-throttled so it stays cheap).
  var hhTicking = false;
  window.addEventListener('scroll', function () {
    if (hhTicking) return;
    hhTicking = true;
    requestAnimationFrame(function () { syncHeaderHeight(); hhTicking = false; });
  }, { passive: true });

  // Re-anchor the account dropdown if the viewport resizes while it's open.
  window.addEventListener('resize', function () {
    if (openId === 'account') anchorAccountDropdown();
  });

  // Escape to close
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && openId) {
      e.preventDefault();
      close();
    }
  });

  // Cart-only: close on any click outside the panel. The cart's backdrop is
  // pointer-transparent (CSS in #overlay-cart) so users can scroll & click on
  // the underlying page; this handler picks up those clicks and dismisses the
  // drawer alongside whatever action the user just took.
  document.addEventListener('mousedown', function (e) {
    if (openId !== 'cart') return;
    var overlay = getOverlay('cart');
    if (!overlay) return;
    var panel = overlay.querySelector('.durnev-overlay__panel');
    if (panel && panel.contains(e.target)) return; // click inside cart — keep open
    if (e.target.closest('[data-overlay-open="cart"]')) return; // cart trigger — let toggle handle it
    close();
  });

  // Public API on window for cart drawer and other code that needs to open programmatically
  window.DurnevOverlays = {
    open: open,
    close: close,
    toggle: toggle,
    get current() { return openId; }
  };
})();
